<script setup lang="ts">
import type { INQUIRY_ITEM, SPOT } from '@/types'
import {
  sendClickedInquiryPreviewEvent,
  sendClickedNextStepsEvent,
} from '@/utils/mixpanel'
import { highlightInquiry } from '@/utils/highlightInquiry'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const appStore = useAppStore()
const inquiriesStore = useInquiriesStore()

const {
  spots,
  selectedSpot,
  showSpotSelection,
  isChangingSpot,
  openSpotSelection,
  selectSpot,
  selectSpotAsAdmin,
  syncAdminSpotInfo,
} = useSpotSelection()

const {
  inquiries,
  isFiltering,
  hasLoaded,
  reload,
  refresh,
  handleFilterInquiries,
} = useInquiriesLoader()

const buttonCollapsed = ref(true)
const newInquiryButton = ref<HTMLElement>()
const showInquiryItemQuickActions = ref(false)
const selectedInquiryItem = ref<INQUIRY_ITEM>()
const showNewInquiryPopUp = ref(false)

const hasMultipleSpots = computed(() => (spots.value?.length ?? 0) > 1)

const handleOpenSettingsPage = () =>
  router.push(`/settings?spot_id=${authStore.spotId}`)

const enterTab = async () => {
  if (hasLoaded.value) return
  if (appStore.iosError) return
  if (!appStore.spotInfo) return
  if (!appStore.spotHopperEmbedded && !authStore.isAuthenticated) return

  if (appStore.isAdmin) {
    const synced = await syncAdminSpotInfo(authStore.spotId)
    if (!synced) return
  }

  await reload()
}

onMounted(enterTab)

const handleSelectedSpot = async (spotId: SPOT['id']) => {
  if (appStore.isAdmin) {
    const changed = await selectSpotAsAdmin(spotId)
    if (changed) await reload()
    return
  }

  await selectSpot(spotId)
}

const handleShowInquiryItemQuickActions = (inquiry: INQUIRY_ITEM) => {
  selectedInquiryItem.value = inquiry
  showInquiryItemQuickActions.value = true
  sendClickedInquiryPreviewEvent(inquiry)
}

const handleShowNewInquiryPopUp = () => {
  showNewInquiryPopUp.value = true
  buttonCollapsed.value = true
}

onClickOutside(newInquiryButton, (event) => {
  if (!buttonCollapsed.value && newInquiryButton.value != event.target) {
    buttonCollapsed.value = true
  }
})

const handleCloseNewInquiryPopUp = async () => {
  showNewInquiryPopUp.value = false
  await refresh()
}

const handleRouteToInquiryDetails = (inquiry: INQUIRY_ITEM) => {
  route.query.inquiry_id = inquiry.id.toString()
  route.query.spot_id = selectedSpot.value.id.toString()

  const { showRespond, showFollowUp } = highlightInquiry(
    inquiry,
    inquiriesStore,
  )

  if (showRespond) {
    sendClickedNextStepsEvent(inquiry, 'Respond')
  }

  if (showFollowUp) {
    sendClickedNextStepsEvent(inquiry, 'Follow-up')
  }

  if (
    inquiry.get_num_of_unread_emails > 0 ||
    (inquiry.description !== null && inquiry.description !== '')
  ) {
    return router.push({
      path: 'inquiry/messages',
      query: route.query,
    })
  }

  return router.push({
    path: 'inquiry/details',
    query: route.query,
  })
}

watch(
  () => [authStore.spotId, authStore.isAuthenticated] as [string, boolean],
  async ([newSpotId, newIsAuthenticated], oldValue) => {
    if (isChangingSpot.value) return
    if (appStore.iosError) return
    if (!appStore.spotInfo) return
    if (!appStore.spotHopperEmbedded && !newIsAuthenticated) return

    if (newSpotId !== oldValue?.[0] && appStore.isAdmin) {
      const synced = await syncAdminSpotInfo(newSpotId)
      if (!synced) return
    }

    await reload()
  },
)
</script>

<template>
  <template v-if="!appStore.isLoading">
    <div class="flex h-dvh flex-col">
      <div class="shrink-0 bg-white shadow-md">
        <AppTopBar :title="selectedSpot?.name ?? appStore.spotInfo?.name">
          <div v-if="appStore.isAdmin" class="w-[104px] shrink-0">
            <AppInput
              v-model="selectedSpot.id"
              class="!text-slate-800"
              variant="neutral"
              name="name"
              type="text"
              @change="handleSelectedSpot(selectedSpot.id)"
            ></AppInput>
          </div>

          <button
            v-else-if="hasMultipleSpots"
            type="button"
            aria-label="Switch spot"
            class="flex h-11 shrink-0 items-center gap-1 rounded-full bg-white/10 px-3 text-white md:h-[50px]"
            @click="openSpotSelection"
          >
            <AppiconCog class="h-5 w-5 fill-white" />
            <HeroiconsChevronDown class="h-5 w-5" />
          </button>

          <button
            v-else
            type="button"
            aria-label="Settings"
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white md:h-[50px] md:w-[50px]"
            @click="handleOpenSettingsPage"
          >
            <AppiconCog class="h-5 w-5 fill-white" />
          </button>
        </AppTopBar>

        <InquiryFilters @update:filters="handleFilterInquiries" />
      </div>

      <InquiryListView
        :inquiries="inquiries"
        :is-loading="isFiltering"
        @select="handleRouteToInquiryDetails"
        @long-press="handleShowInquiryItemQuickActions"
      />

      <HuiTransitionRoot
        as="template"
        :show="buttonCollapsed"
        enter="ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <AppButton
          tabindex="-1"
          class="fixed bottom-4 right-4 h-12 w-12 !shadow-newInq transition-all duration-300 ease-in-out"
          size="md"
          variant="error"
          :is-rounded="true"
          :is-wide="false"
        >
          <AppiconPlus class="fill-white" @click="buttonCollapsed = false" />
        </AppButton>
      </HuiTransitionRoot>

      <HuiTransitionRoot
        enter="ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        as="template"
        :show="!buttonCollapsed"
      >
        <AppButton
          ref="newInquiryButton"
          tabindex="-1"
          class="fixed bottom-4 right-4 !shadow-newInq transition-all duration-300 ease-in-out"
          size="md"
          variant="error"
          :is-rounded="true"
          :is-wide="false"
        >
          <span @click="handleShowNewInquiryPopUp">Add New Inquiry</span>
          <AppiconPlus class="white h-3.5 w-3.5 fill-white" />
        </AppButton>
      </HuiTransitionRoot>
    </div>

    <SpotSelector
      :is-open="showSpotSelection"
      :spots="spots"
      :selected-spot="selectedSpot"
      @close="showSpotSelection = false"
      @selected="handleSelectedSpot"
    />

    <InquiryItemQuickActions
      v-if="selectedInquiryItem"
      :inquiry="selectedInquiryItem"
      :show="showInquiryItemQuickActions"
      @close="showInquiryItemQuickActions = false"
    />

    <InquiryDrawer
      :show="showNewInquiryPopUp"
      @close="handleCloseNewInquiryPopUp"
    >
      <template #title>
        <div class="text-center">
          <p class="font-bold text-gray-500">New Inquiry</p>
        </div>
      </template>
      <template #content>
        <InquiryItem @close="handleCloseNewInquiryPopUp" />
      </template>
    </InquiryDrawer>
  </template>
  <AppLoader v-else />
</template>
